import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import CircuitCountriesForm from './CircuitCountriesForm';
import StyledDropZone from '../styled/StyledDropZone';
import CircuitAdditionalParagraphs from './CircuitAdditionalParagraphs';
import CircuitParticipantsSection from './CircuitParticipantsSection';
import { CIRCUIT_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import TranslateIcon from '../translation/TranslateIcon';
import { TRANSLATABLE_FIELDS } from '@/i18n/translatable-fields';
import { useState } from 'react';
import Modal from '../ui/Modal';
import { ParagraphOverrides } from '@/api/circuits.api';
import clsx from 'clsx';
import { useParagraphOverrideHighlight } from '@/hooks/useParagraphOverrideHighlight';

interface CircuitConfigurationProps {
  control: Control<CircuitFormDataWithTranslations>;
  isEditEnabled: boolean;
  language: SupportedLocale;
  translateFields: (
    fields: (typeof TRANSLATABLE_FIELDS.Circuit)[number][],
  ) => Promise<void>;
  isTranslating: boolean;
  paragraphOverrides?: ParagraphOverrides;
  suppressedOverrideFields?: Set<string>;
}

const CircuitConfiguration = ({
  control,
  isEditEnabled,
  language,
  translateFields,
  isTranslating,
  paragraphOverrides,
  suppressedOverrideFields,
}: CircuitConfigurationProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { setValue } = useFormContext<CircuitFormDataWithTranslations>();
  const { hasOverride } = useParagraphOverrideHighlight({
    paragraphOverrides,
    language,
    control,
    suppressedOverrideFields,
  });

  const [warningField, setWarningField] = useState<string | null>(null);
  const [acknowledgedFields, setAcknowledgedFields] = useState<Set<string>>(
    new Set(),
  );

  const handleFieldFocus = (field: string) => {
    if (hasOverride(field) && !acknowledgedFields.has(field)) {
      setWarningField(field);
    }
  };

  const handleModalCancel = () => {
    setWarningField(null);
  };

  const handleModalClose = () => {
    if (warningField) {
      setAcknowledgedFields((prev) => new Set(prev).add(warningField));
    }
    setWarningField(null);
  };

  const OverrideWrapper = ({
    field,
    children,
  }: {
    field: string;
    children: React.ReactNode;
  }) => (
    <div
      className={clsx({
        'bg-red-50 rounded-lg ring-1 ring-red-200 p-1': hasOverride(field),
      })}
      onFocus={() => handleFieldFocus(field)}
    >
      {children}
    </div>
  );

  const warningOverrides = warningField
    ? (paragraphOverrides?.[warningField] ?? [])
    : [];

  return (
    <>
      <StyledFormSection
        sectionTitle={t('circuits.configuration.generalInfo')}
        icon={
          <TranslateIcon
            translateFields={() => translateFields(['name'])}
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        <div className="flex flex-col md:flex-row gap-4">
          <StyledTranslatableInput
            control={control}
            name="name"
            originalName="name"
            language={language}
            setValue={setValue}
            title={t('circuits.configuration.name')}
            placeholder={t('circuits.configuration.namePlaceholder')}
            disabled={!isEditEnabled}
          />
          <StyledInput
            control={control}
            name="remarks"
            title={t('circuits.configuration.remarks')}
            placeholder={t('circuits.configuration.remarksPlaceholder')}
            disabled={!isEditEnabled}
          />
        </div>
        <CircuitCountriesForm control={control} isViewMode={!isEditEnabled} />
        <MarginRateStyledInput
          control={control}
          name="marginRate"
          title={getTranslatedConst(CIRCUIT_MARGIN_RATE_LABEL)}
          placeholder={t('circuits.configuration.addRate')}
          disabled={!isEditEnabled}
        />
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection sectionTitle={t('circuits.configuration.duration')}>
        <div className="flex flex-col md:flex-row gap-4">
          <StyledInput
            control={control}
            name="nbDays"
            type="number"
            title={t('circuits.configuration.numberOfDays')}
            min={1}
            disabled={!isEditEnabled}
          />
          <StyledInput
            control={control}
            name="nbNights"
            type="number"
            title={t('circuits.configuration.numberOfNights')}
            min={0}
            disabled={!isEditEnabled}
          />
        </div>
      </StyledFormSection>
      <HorizontalLine />
      <CircuitParticipantsSection
        control={control}
        isViewMode={!isEditEnabled}
      />
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('circuits.configuration.description')}
        icon={
          <TranslateIcon
            translateFields={() =>
              translateFields([
                'summary',
                'importantPoints',
                'priceIncludes',
                'priceExcludes',
              ])
            }
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        <StyledDropZone
          control={control}
          name="mainImage"
          title={t('circuits.configuration.mainImage')}
          placeholder={t('circuits.configuration.mainImagePlaceholder')}
          disabled={!isEditEnabled}
        />
        <StyledDropZone
          control={control}
          name="images"
          title={t('circuits.configuration.gallery')}
          placeholder={t('circuits.configuration.galleryPlaceholder')}
          multiple
          disabled={!isEditEnabled}
        />
        <OverrideWrapper field="summary">
          <StyledTranslatableTextarea
            control={control}
            name="summary"
            originalName="summary"
            setValue={setValue}
            language={language}
            title={t('circuits.configuration.summary')}
            placeholder={t('circuits.configuration.summaryPlaceholder')}
            rows={3}
            disabled={!isEditEnabled}
          />
        </OverrideWrapper>
        <OverrideWrapper field="importantPoints">
          <StyledTranslatableTextarea
            control={control}
            name="importantPoints"
            originalName="importantPoints"
            setValue={setValue}
            language={language}
            title={t('circuits.configuration.importantPoints')}
            placeholder={t('circuits.configuration.importantPointsPlaceholder')}
            rows={3}
            disabled={!isEditEnabled}
          />
        </OverrideWrapper>
        <CircuitAdditionalParagraphs
          control={control}
          isViewMode={!isEditEnabled}
        />

        <OverrideWrapper field="priceIncludes">
          <StyledTranslatableTextarea
            control={control}
            name="priceIncludes"
            originalName="priceIncludes"
            setValue={setValue}
            language={language}
            title={t('circuits.configuration.priceIncludes')}
            placeholder={t('circuits.configuration.priceIncludesPlaceholder')}
            rows={3}
            disabled={!isEditEnabled}
          />
        </OverrideWrapper>
        <OverrideWrapper field="priceExcludes">
          <StyledTranslatableTextarea
            control={control}
            name="priceExcludes"
            originalName="priceExcludes"
            setValue={setValue}
            language={language}
            title={t('circuits.configuration.priceExcludes')}
            placeholder={t('circuits.configuration.priceExcludesPlaceholder')}
            rows={3}
            disabled={!isEditEnabled}
          />
        </OverrideWrapper>
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('circuits.configuration.modificationConditions')}
        icon={
          <TranslateIcon
            translateFields={() =>
              translateFields([
                'modificationConditions',
                'cancellationConditions',
              ])
            }
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        <OverrideWrapper field="modificationsConditions">
          <StyledTranslatableTextarea
            control={control}
            name="modificationConditions"
            originalName="modificationsConditions"
            setValue={setValue}
            language={language}
            title={t('circuits.configuration.modificationConditionsTitle')}
            placeholder={t(
              'circuits.configuration.modificationConditionsPlaceholder',
            )}
            rows={3}
            disabled={!isEditEnabled}
          />
        </OverrideWrapper>
        <OverrideWrapper field="cancellationConditions">
          <StyledTranslatableTextarea
            control={control}
            name="cancellationConditions"
            originalName="cancellationConditions"
            setValue={setValue}
            language={language}
            title={t('circuits.configuration.cancellationConditions')}
            placeholder={t(
              'circuits.configuration.cancellationConditionsPlaceholder',
            )}
            rows={3}
            disabled={!isEditEnabled}
          />
        </OverrideWrapper>
      </StyledFormSection>
      {warningField && (
        <Modal
          closeModal={handleModalCancel}
          title={t('circuits.paragraphOverridesWarning.title')}
          message={t('circuits.paragraphOverridesWarning.message')}
          actionTitle={t('common.continue')}
          actionOnClick={handleModalClose}
          hasAction
          cancelBtnLabel={t('common.cancel')}
        >
          {warningOverrides.length > 0 && (
            <ul className="mt-2 text-sm text-gray-700 list-disc pl-5">
              {warningOverrides.map((entry, i) => (
                <li key={i}>
                  {entry.distributorName}
                  {entry.entityType === 'editableCircuit'
                    ? ` — ${entry.entityRef}`
                    : ` — ${t('circuits.paragraphOverridesWarning.estimateRef', { ref: entry.entityRef })}`}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </>
  );
};

export default CircuitConfiguration;
